import type { DropdownListGroup } from "@/components/ui/dropdown-list/dropdown-list";
import type { VariableType } from "./calculated-field/CalculatedFieldOperation/types";
import { NodeTypes } from "@/workflow_api/types/workflow";

export type NodeVariable = {
  nodeId: string;
  name: string;
  type: (typeof NodeTypes)[keyof typeof NodeTypes] | "global-variables";
  variables: Variable[];
  ref_name?: string;
  source?: string; //for database source
};

export type Variable = {
  id: string; // field id
  name: string;
  type: VariableType;
  data?: object;
  node_id?: string;
  field_id?: string;
  ref_name?: string;
  meta_type?: string;
  output_name?: string;
  value?: string;
  global_type?:
    | "APPLICANT"
    | "NODE_EXECUTION_START"
    | "NODE_EXECUTION_FINISH"
    | "CURRENT_TIME";
};

export const getNodeIcon = (
  type: NodeVariable["type"],
): DropdownListGroup["icon"] => {
  switch (type) {
    case "global-variables":
      return {
        type: "far",
        name: "globe",
        variant: "neutral",
      };
    case NodeTypes.INITIALIZER:
      return {
        type: "fas",
        name: "play",
        variant: "neutral",
      };
    case NodeTypes.IF:
      return {
        type: "fas",
        name: "split",
        variant: "neutral",
      };
    case NodeTypes.ASSIGN_GROUP: {
      return {
        type: "fas",
        name: "users",
        variant: "info",
        className: "text-info-quiet",
      };
    }
    case NodeTypes.FORM:
      return {
        type: "fas",
        name: "bars",
        variant: "action",
        className: "text-action-quiet",
      };
    case NodeTypes.SWITCH:
      return {
        type: "fas",
        name: "arrows-cross",
        variant: "neutral",
      };
    case NodeTypes.CALCULATED: {
      return {
        type: "fas",
        name: "function",
        variant: "success",
        className: "text-success-quiet",
      };
    }
    case NodeTypes.DATA_CENTER: {
      return {
        type: "fas",
        name: "table",
        variant: "info",
        className: "text-info-quiet",
      };
    }
    case NodeTypes.EXPEDIENTE: {
      return {
        type: "fas",
        name: "folder-open",
        variant: "info",
        className: "text-info-quiet",
      };
    }
    case NodeTypes.SIGN_DOCUMENT: {
      return {
        type: "fas",
        name: "signature",
        variant: "action",
        className: "text-action-quiet",
      };
    }
    case NodeTypes.NOTIFICATION: {
      return {
        type: "fas",
        name: "bell",
        variant: "info",
        className: "text-info-quiet",
      };
    }
    case NodeTypes.DOC_GENERATOR: {
      return {
        type: "fas",
        name: "file-lines",
        variant: "action",
        className: "text-action-quiet",
      };
    }
    case NodeTypes.INTEROPERABILITY: {
      return {
        type: "fas",
        name: "diagram-nested",
        variant: "warning",
        className: "text-warning-quiet",
      };
    }
    case NodeTypes.MERGE: {
      return {
        type: "fas",
        name: "code-merge",
        variant: "info",
        className: "text-info-quiet",
      };
    }
    case NodeTypes.N8N: {
      return {
        type: "fas",
        name: "hexagon-nodes",
        variant: "warning",
        className: "text-warning-quiet",
      };
    }
    case NodeTypes.PAYMENT: {
      return {
        type: "fas",
        name: "dollar-sign",
        variant: "warning",
        className: "text-warning-quiet",
      };
    }
    default: {
      return {
        type: "fas",
        name: "question",
        variant: "neutral",
      };
    }
  }
};

export const getVariableIcon = (
  type: Variable["type"],
): DropdownListGroup["items"][number]["icon"] => {
  switch (type) {
    case "string":
      return { type: "far", name: "text" };
    case "float":
    case "number":
      return { type: "far", name: "hashtag" };
    case "boolean":
      return { type: "far", name: "toggle-large-on" };
    case "date":
      return { type: "far", name: "calendar" };
    case "array":
      return { type: "far", name: "list" };
    case "file":
      return { type: "far", name: "file" };
    default:
      return { type: "far", name: "question" };
  }
};

export const nodeVariableToGroups = (
  nodes: NodeVariable[],
): DropdownListGroup[] => {
  return nodes.map((n) => {
    return {
      groupId: n.nodeId,
      groupLabels: [n.name],
      icon: getNodeIcon(n.type),
      items: n.variables.map((v) => ({
        label: v.name,
        value: v.id,
        detail: v.type,
        type: v.type,
        icon: getVariableIcon(v.type),
      })),
    };
  });
};
